// Administration → Utilisateurs: the accounts table (cards on phones; an « AJOUTE DES
// CONNECTEURS » badge for the members allowed to add connectors to the office's library,
// « CONNECTEURS LIMITÉS » for those whose office connectors are restricted), the create form
// (temporary password shown once), the edit form (display name, role, active, grants, the « Peut
// ajouter des connecteurs » permission and the « Connecteurs autorisés » — a note instead for an
// administrator, who may always add and use everything —, reset password, delete) with the
// self-protection rules of SPEC §7 mirrored in the UI (the server enforces them too). The
// connectors of the checklist — the detected ones and the library's, with who added them — come
// from GET /api/connectors, loaded with the tab and again on the `connectors` event. « Voir
// l’activité » on a row opens the « Activité » tab on that person (when the sheet has it).
import { h, button, input, field, segmented, switchToggle, badge } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { isConflict } from '../../api.js';
import { relativeTime, formatDateTime, initials, hashHue } from '../../format.js';
import {
  validateUser, userPatch, selfGuards, userStatus, sortUsers, filterUsers, grantsSummary, grantLines,
  normalizeUsername, LIMITS, policyRestricts, policySummary,
} from './model.js';
import { createGrantsEditor } from './grants.js';
import { createPolicyControl } from './policy.js';
import { libraryAccess } from '../myconnectors/model.js';
import { sectionHead, loadingState, errorState, copyButton, formError, setBusy, focusKeeper, aid } from './common.js';
import './strings.js';

/**
 * Shows a temporary password once, with a copy button and what to do with it.
 * @param {object} ctx
 * @param {{username: string, displayName?: string}} user
 * @param {string} password
 */
export function showTempPassword(ctx, user, password) {
  const name = user.displayName || user.username;
  const pwInput = h('input', {
    class: 'input adm-temp-input', type: 'text', readOnly: true, value: password, autocomplete: 'off',
    spellcheck: false, 'aria-label': t('adm.temp.label'),
  });
  pwInput.addEventListener('focus', () => pwInput.select?.());
  const copy = copyButton(() => pwInput.value, { toast: ctx.toast, selectEl: pwInput, size: 'md', variant: 'primary' });
  let layer = null;
  const done = button(t('adm.temp.done'), { variant: 'secondary', onClick: () => layer?.close('confirm') });
  const content = h('div', { class: 'stack adm-temp' },
    h('p', { class: 'adm-temp-once' }, t('adm.temp.once')),
    h('div', { class: 'adm-temp-row' }, pwInput, copy),
    h('ul', { class: 'adm-temp-list' },
      h('li', {}, t('adm.temp.share', { name })),
      h('li', {}, t('adm.temp.change', { name }))));
  layer = ctx.overlay.openModal({
    title: t('adm.temp.title'), subtitle: t('adm.temp.subtitle', { username: user.username }), size: 'sm',
    content, footer: [done], initialFocus: copy, className: 'adm-temp-modal',
  });
  // The password only lives in this dialog: wipe it from the DOM when it closes.
  layer.onClose?.(() => { pwInput.value = ''; });
  return layer;
}

/** The role segmented control + its hint, as a labelled group. */
function roleControl(value, { disabled = false, onChange } = {}) {
  const labelId = aid('role');
  const hint = h('p', { class: 'field-hint' });
  const seg = segmented([
    { value: 'member', label: t('adm.user.role.member') },
    { value: 'admin', label: t('adm.user.role.admin') },
  ], { value, label: t('adm.user.role'), onChange: (v) => { sync(v); onChange?.(v); } });
  seg.setAttribute('aria-labelledby', labelId);
  const sync = (v) => { hint.textContent = t(v === 'admin' ? 'adm.user.roleHint.admin' : 'adm.user.roleHint.member'); };
  if (disabled) for (const b of seg.childNodes) b.disabled = true;
  sync(value);
  const el = h('div', { class: 'field' }, h('span', { class: 'field-label', id: labelId }, t('adm.user.role')), seg, hint);
  el.control = seg;
  return el;
}

/**
 * The « Peut ajouter des connecteurs » permission of the user form (stored as `personalConnectors`,
 * alias `canAddConnectors`): a switch for a member, a note in its place for an administrator
 * (always allowed). The switch is stored even while the server has the library off, and its hint
 * says so.
 * @param {boolean} checked
 * @param {{role: string, featureOn: boolean}} opts
 * @returns {{el: HTMLElement, input: HTMLInputElement, setRole(role: string): void}}
 */
function personalControl(checked, { role, featureOn }) {
  const hint = featureOn ? t('adm.user.personalHint') : `${t('adm.user.personalHint')} ${t('adm.user.personalOff')}`;
  const sw = switchToggle(t('adm.user.personal'), { checked, hint });
  const box = h('div', { class: 'field adm-personal-field' }, sw);
  const note = h('p', { class: 'adm-note' }, t('adm.user.personalAdmin'));
  const setRole = (r) => { box.hidden = r === 'admin'; note.hidden = r !== 'admin'; };
  setRole(role);
  return { el: h('div', { class: 'adm-personal' }, box, note), input: sw.input, setRole };
}

/**
 * Creates the users tab.
 * @param {object} ctx app context
 * @param {{has?: (tab: string) => boolean, open?: (tab: string, params?: object) => boolean}} [nav]
 *   the admin sheet's navigation (« Voir l’activité » only when it has the « Activité » tab)
 * @returns {{el: HTMLElement, reload(): Promise<void>, destroy(): void}}
 */
export function createUsersView(ctx, nav = {}) {
  const canShowActivity = typeof nav?.open === 'function' && nav.has?.('activity') === true;
  const office = () => ctx.store.get().office;
  const me = () => ctx.store.get().me;
  const personalOn = () => libraryAccess(ctx.store.get()).enabled;
  // The permission as the server reports it (`canAddConnectors`, or its former name).
  const mayAdd = (u) => (typeof u?.canAddConnectors === 'boolean' ? u.canAddConnectors : u?.personalConnectors === true);
  let users = null;
  let error = null;
  let query = '';
  let destroyed = false;
  let servers = null;          // GET /api/connectors servers (the « Connecteurs autorisés » checklist)
  let serversFailed = false;
  const policyControls = new Set(); // the controls of the open forms, filled when the list arrives
  const keeper = focusKeeper();

  const search = input({
    type: 'search', class: 'input-sm adm-search', placeholder: t('adm.users.search'), 'aria-label': t('adm.users.search'),
    onInput: (e) => { query = e.target.value; render(); },
  });
  const newBtn = button(t('adm.users.new'), { variant: 'primary', icon: 'plus', onClick: () => openCreate() });
  const head = sectionHead({ title: t('adm.users.title'), intro: t('adm.users.intro'), actions: [search, newBtn] });
  const body = h('div', { class: 'adm-body' }, loadingState());
  const el = h('section', { class: 'adm-view adm-users' }, head, body);

  async function load() {
    try {
      const list = await ctx.api.get('/api/users');
      if (destroyed) return;
      users = Array.isArray(list) ? list : Array.isArray(list?.users) ? list.users : [];
      error = null;
    } catch (err) {
      if (destroyed) return;
      error = err;
    }
    render();
  }

  async function loadConnectors() {
    try {
      const res = await ctx.api.get('/api/connectors');
      if (destroyed) return;
      servers = Array.isArray(res) ? res : Array.isArray(res?.servers) ? res.servers : [];
      serversFailed = false;
    } catch {
      if (destroyed) return;
      serversFailed = true;
    }
    for (const c of policyControls) c.setServers(servers, { failed: serversFailed });
    if (users) render();
  }

  /** A « Connecteurs autorisés » control for a form, with the connectors known so far. */
  function policyControl(policy, role) {
    return createPolicyControl(policy, { role, servers, failed: serversFailed });
  }

  /** Keeps a form's control up to date with the connectors list while its window is open. */
  function trackPolicy(control, layer) {
    policyControls.add(control);
    layer?.onClose?.(() => policyControls.delete(control));
  }

  /** Names of the connectors for summaries: the admin list, else the bootstrap chips. */
  const connectorNames = () => servers || ctx.store.get().connectors || [];

  function render() {
    const restore = keeper.begin();
    if (error && !users) {
      head.setCount('');
      body.replaceChildren(errorState(error, { onRetry: () => { body.replaceChildren(loadingState()); load(); }, unavailableText: t('adm.users.unavailable') }));
      newBtn.disabled = error.status === 404;
      return;
    }
    if (!users) return;
    newBtn.disabled = false;
    head.setCount(t('adm.users.count', { n: users.length }));
    const list = filterUsers(sortUsers(users), query);
    if (!list.length) {
      body.replaceChildren(h('p', { class: 'adm-empty-line muted' }, t('adm.users.empty')));
      restore();
      return;
    }
    const th = (key) => h('th', { scope: 'col' }, t(`adm.users.col.${key}`));
    body.replaceChildren(h('div', { class: 'adm-table-wrap' },
      h('table', { class: 'table adm-table adm-users-table' },
        h('thead', {}, h('tr', {}, th('name'), th('role'), th('grants'), th('lastLogin'), th('status'),
          h('th', { scope: 'col' }, h('span', { class: 'sr-only' }, t('adm.actions'))))),
        h('tbody', {}, list.map(userRow)))));
    restore();
  }

  function userRow(u) {
    const self = me()?.id === u.id;
    const st = userStatus(u);
    const name = u.displayName || u.username;
    const lines = grantLines(u.grants, office());
    const edit = keeper.reg(`edit:${u.id}`, button(t('adm.users.edit'), {
      variant: 'secondary', size: 'sm', ariaLabel: t('adm.users.editAria', { name }), onClick: () => openEdit(u),
    }));
    const activity = canShowActivity ? keeper.reg(`activity:${u.id}`, button(t('adm.users.activity'), {
      variant: 'link', size: 'sm', class: 'adm-user-activity', ariaLabel: t('adm.users.activityAria', { name }),
      onClick: () => nav.open('activity', { userId: u.id, userName: name }),
    })) : null;
    const last = u.lastLoginAt
      ? h('time', { datetime: String(u.lastLoginAt), title: formatDateTime(u.lastLoginAt) }, relativeTime(u.lastLoginAt))
      : h('span', { class: 'muted' }, t('adm.never'));
    return h('tr', { class: ['adm-user-row', { 'is-disabled': u.disabled, 'is-self': self }], dataset: { id: u.id } },
      h('td', { class: 'adm-cell-main', 'data-label': t('adm.users.col.name') },
        h('div', { class: 'adm-user' },
          h('span', { class: 'adm-avatar', 'aria-hidden': 'true', style: { '--hue': String(hashHue(u.username)) } }, initials(name)),
          h('span', { class: 'adm-user-text' },
            h('span', { class: 'adm-user-name' }, name, self ? h('span', { class: 'adm-you mono-label' }, t('adm.you')) : null),
            h('span', { class: 'adm-user-handle' }, `@${u.username}`)))),
      h('td', { 'data-label': t('adm.users.col.role') }, h('span', { class: 'adm-user-badges' },
        badge(t(u.role === 'admin' ? 'role.admin' : 'role.member'), u.role === 'admin' ? 'dark' : 'neutral'),
        // A member allowed to add connectors to the library (an administrator always is: no badge).
        u.role !== 'admin' && mayAdd(u)
          ? badge(t('adm.users.personal'), 'info', { class: 'adm-user-pconn', title: t('adm.users.personalHint') }) : null,
        // A member whose office connectors are restricted (an administrator never is: no badge).
        u.role !== 'admin' && policyRestricts(u.connectorPolicy)
          ? badge(t('adm.users.limited'), 'amber', {
            class: 'adm-user-climit', title: t('adm.users.limitedHint', { summary: policySummary(u.connectorPolicy, connectorNames()) }),
          }) : null)),
      h('td', { class: 'adm-user-grants', 'data-label': t('adm.users.col.grants'), title: lines.map((l) => l.text).join('\n') || null },
        grantsSummary(u, office())),
      h('td', { 'data-label': t('adm.users.col.lastLogin') }, last),
      h('td', { 'data-label': t('adm.users.col.status') },
        badge(st.label, st.tone, { title: st.key === 'mustChange' ? t('adm.status.mustChangeHint') : undefined })),
      h('td', { class: 'adm-cell-actions' }, activity, edit));
  }

  function upsertUser(user) {
    if (!user?.id || !users) return;
    users = users.some((u) => u.id === user.id) ? users.map((u) => (u.id === user.id ? user : u)) : [...users, user];
    if (me()?.id === user.id) ctx.store.set({ me: { ...me(), ...user } });
  }

  // --- create -------------------------------------------------------------------------------
  function openCreate() {
    const usernameInput = input({
      name: 'username', autocomplete: 'off', autocapitalize: 'none', spellcheck: false, maxLength: 32, autofocus: true,
      onBlur: () => { usernameInput.value = normalizeUsername(usernameInput.value); },
    });
    const fUser = field(t('adm.user.username'), usernameInput, { hint: t('adm.user.usernameHint') });
    const nameInput = input({ name: 'displayName', autocomplete: 'off', maxLength: LIMITS.displayName });
    const fName = field(t('adm.user.displayName'), nameInput, { hint: t('adm.user.displayNameHint'), optional: true });
    const grants = createGrantsEditor({ office: office() });
    const grantsField = h('div', { class: 'field adm-grants-field' }, h('span', { class: 'field-label' }, t('adm.user.grants')), grants.el);
    const adminNote = h('p', { class: 'adm-note', hidden: true }, t('adm.user.grantsAdmin'));
    const personal = personalControl(false, { role: 'member', featureOn: personalOn() });
    const policy = policyControl(null, 'member');
    const role = roleControl('member', {
      onChange: (v) => {
        grantsField.hidden = v === 'admin'; adminNote.hidden = v !== 'admin'; personal.setRole(v); policy.setRole(v);
      },
    });
    const err = formError();
    const formId = aid('form');
    let layer = null;
    const submitBtn = button(t('adm.user.create'), { variant: 'primary', type: 'submit' });
    submitBtn.setAttribute('form', formId);
    const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('cancel') });

    async function submit(e) {
      e?.preventDefault?.();
      const res = validateUser({
        username: usernameInput.value, displayName: nameInput.value, role: role.control.value, grants: grants.getValue(),
        personalConnectors: personal.input.checked, connectorPolicy: policy.getValue(),
      }, { isNew: true, office: office() });
      fUser.setError(res.errors.username || null);
      fName.setError(res.errors.displayName || null);
      grants.showProblems(res.errors.grants || []);
      if (!res.ok) {
        err.set(t('adm.formError'));
        (res.errors.username ? usernameInput : res.errors.displayName ? nameInput : null)?.focus();
        return;
      }
      err.set(null);
      const payload = { username: res.value.username, role: res.value.role, grants: res.value.grants };
      if (res.value.displayName) payload.displayName = res.value.displayName;
      // A member's permission, sent when given (the server's default is no).
      if (res.value.personalConnectors === true) payload.personalConnectors = true;
      // A member's restriction, sent when there is one (the server's default is « Tous »).
      if (res.value.connectorPolicy && res.value.connectorPolicy.mode !== 'all') payload.connectorPolicy = res.value.connectorPolicy;
      setBusy(submitBtn, true);
      try {
        const out = await ctx.api.post('/api/users', payload);
        const user = out?.user || { username: payload.username, displayName: payload.displayName };
        layer?.close('api');
        upsertUser(out?.user);
        render();
        ctx.toast.success(t('adm.user.created', { username: user.username }));
        if (out?.tempPassword) showTempPassword(ctx, user, out.tempPassword);
        load();
      } catch (ex) {
        setBusy(submitBtn, false);
        if (isConflict(ex) || /identifiant/i.test(ex.message || '')) { fUser.setError(ex.message); usernameInput.focus(); } else err.set(ex.message);
      }
    }

    const form = h('form', { id: formId, class: 'stack adm-form', novalidate: true, onSubmit: submit },
      fUser, fName, role, grantsField, adminNote, personal.el, policy.el, err);
    layer = ctx.overlay.openModal({
      title: t('adm.user.createTitle'), subtitle: t('adm.user.createSubtitle'), size: 'md', content: form,
      footer: [cancel, submitBtn], className: 'adm-modal',
    });
    trackPolicy(policy, layer);
    return layer;
  }

  // --- edit -----------------------------------------------------------------------------------
  function openEdit(u) {
    const guards = selfGuards(me(), u);
    const name = u.displayName || u.username;
    const nameInput = input({ name: 'displayName', value: u.displayName || '', autocomplete: 'off', maxLength: LIMITS.displayName, autofocus: true });
    const fName = field(t('adm.user.displayName'), nameInput, { hint: t('adm.user.displayNameHint') });
    const grants = createGrantsEditor({ office: office(), grants: u.grants || [] });
    const grantsField = h('div', { class: 'field adm-grants-field', hidden: u.role === 'admin' },
      h('span', { class: 'field-label' }, t('adm.user.grants')), grants.el);
    const adminNote = h('p', { class: 'adm-note', hidden: u.role !== 'admin' }, t('adm.user.grantsAdmin'));
    // An administrator shows true (always allowed): demoted, he starts without it unless switched on.
    const personal = personalControl(u.role !== 'admin' && mayAdd(u), { role: u.role, featureOn: personalOn() });
    // An administrator shows « Tous » (never restricted): demoted, he starts unrestricted unless set here.
    const policy = policyControl(u.role === 'admin' ? null : u.connectorPolicy, u.role);
    const role = roleControl(u.role === 'admin' ? 'admin' : 'member', {
      disabled: !guards.canDemote,
      onChange: (v) => {
        grantsField.hidden = v === 'admin'; adminNote.hidden = v !== 'admin'; personal.setRole(v); policy.setRole(v);
      },
    });
    const active = switchToggle(t('adm.user.active'), { checked: !u.disabled, disabled: !guards.canDisable, hint: t('adm.user.activeHint') });
    const err = formError();
    const formId = aid('form');
    let layer = null;
    const saveBtn = button(t('common.save'), { variant: 'primary', type: 'submit' });
    saveBtn.setAttribute('form', formId);
    const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('cancel') });

    const resetBtn = button(t('adm.user.resetPassword'), { variant: 'secondary', size: 'sm', icon: 'key', disabled: !guards.canReset, onClick: () => reset() });
    const deleteBtn = guards.canDelete
      ? button(t('adm.user.delete'), { variant: 'danger', size: 'sm', onClick: () => remove() })
      : null;

    async function submit(e) {
      e?.preventDefault?.();
      const res = validateUser({ displayName: nameInput.value, role: role.control.value, grants: grants.getValue() },
        { isNew: false, office: office() });
      fName.setError(res.errors.displayName || null);
      grants.showProblems(res.errors.grants || []);
      if (!res.ok) { err.set(t('adm.formError')); return; }
      err.set(null);
      const patch = userPatch({ ...u, personalConnectors: mayAdd(u) }, {
        displayName: nameInput.value, role: res.value.role, disabled: !active.input.checked, grants: res.value.grants,
        personalConnectors: personal.input.checked, connectorPolicy: policy.getValue(),
      });
      if (!guards.canDemote) delete patch.role;
      if (!guards.canDisable) delete patch.disabled;
      if (!Object.keys(patch).length) { layer?.close('confirm'); ctx.toast.info(t('adm.user.noChange')); return; }
      setBusy(saveBtn, true);
      try {
        const out = await ctx.api.patch(`/api/users/${encodeURIComponent(u.id)}`, patch);
        layer?.close('api');
        upsertUser(out?.user);
        render();
        ctx.toast.success(t('adm.saved'));
        load();
      } catch (ex) {
        setBusy(saveBtn, false);
        err.set(ex.message);
      }
    }

    async function reset() {
      const ok = await ctx.overlay.confirm({
        title: t('adm.user.resetConfirmTitle'), message: t('adm.user.resetConfirm', { name }),
        confirmLabel: t('adm.user.resetConfirmLabel'), danger: true,
      });
      if (!ok) return;
      setBusy(resetBtn, true);
      try {
        const out = await ctx.api.patch(`/api/users/${encodeURIComponent(u.id)}`, { resetPassword: true });
        setBusy(resetBtn, false);
        upsertUser(out?.user);
        render();
        if (out?.tempPassword) showTempPassword(ctx, out.user || u, out.tempPassword);
      } catch (ex) {
        setBusy(resetBtn, false);
        err.set(ex.message);
      }
    }

    async function remove() {
      const ok = await ctx.overlay.confirm({
        title: t('adm.user.deleteConfirmTitle', { name }), message: t('adm.user.deleteConfirm', { username: u.username }),
        confirmLabel: t('common.delete'), danger: true,
      });
      if (!ok) return;
      setBusy(deleteBtn, true);
      try {
        await ctx.api.del(`/api/users/${encodeURIComponent(u.id)}`);
        layer?.close('api');
        users = (users || []).filter((x) => x.id !== u.id);
        render();
        ctx.toast.success(t('adm.user.deleted', { username: u.username }));
        newBtn.focus?.();
        load();
      } catch (ex) {
        setBusy(deleteBtn, false);
        err.set(ex.message);
      }
    }

    const form = h('form', { id: formId, class: 'stack adm-form', novalidate: true, onSubmit: submit },
      guards.isSelf ? h('p', { class: 'adm-note' }, t('adm.user.selfHint')) : null,
      u.mustChangePassword ? h('p', { class: 'adm-note is-amber' }, t('adm.user.mustChangeNote')) : null,
      fName, role, h('div', { class: 'field' }, active), grantsField, adminNote, personal.el, policy.el,
      h('section', { class: 'adm-subsection' },
        h('h4', { class: 'adm-subtitle mono-label' }, t('adm.user.password')),
        h('div', { class: 'adm-subsection-row' }, resetBtn,
          h('p', { class: 'field-hint' }, guards.canReset ? t('adm.user.resetHint') : t('adm.user.resetSelf')))),
      deleteBtn ? h('section', { class: 'adm-subsection adm-danger-zone' },
        h('h4', { class: 'adm-subtitle mono-label' }, t('adm.user.danger')),
        h('div', { class: 'adm-subsection-row' }, deleteBtn, h('p', { class: 'field-hint' }, t('adm.user.deleteHint')))) : null,
      err);
    const created = u.createdAt ? relativeTime(u.createdAt) : '—';
    layer = ctx.overlay.openModal({
      title: t('adm.user.editTitle', { name }),
      subtitle: [t('adm.user.editSubtitle', { username: u.username, created }),
        u.lastLoginAt ? t('adm.user.lastLogin', { when: relativeTime(u.lastLoginAt) }) : null].filter(Boolean).join(' · '),
      size: 'md', content: form, footer: [cancel, saveBtn], className: 'adm-modal',
    });
    trackPolicy(policy, layer);
    return layer;
  }

  const offs = [
    ctx.store.on('office', () => { if (users) render(); }),
    ctx.events.on('connectors', () => loadConnectors()),
  ];
  load();
  loadConnectors();
  return {
    el,
    reload: () => { loadConnectors(); return load(); },
    openCreate,
    destroy() {
      destroyed = true;
      for (const off of offs) off();
      policyControls.clear();
    },
  };
}
